import * as React from "react";
import { FC, useEffect } from "react";
import * as Yup from "yup";
import { Field, Form, FormikValues } from "formik";
import FormikForm from "~/lib/forms/FormikForm";
import Input from "~/common/controls/Input";
import Button from "~/common/elements/mobile/Button";
import { buttonSize, buttonStyle } from "~/common/elements/mobile/consts";
import ModelSelect from "~/common/controls/ModelSelect";
import { SearchableModelHelper } from "~/lib/forms/select";
import { ProductTypeOptions, ProductTypes } from "~/apps/pos/consts";
import Select from "~/common/controls/Select";
import decimal from "~/lib/normalize/decimal";
import Decimal from "decimal.js";

const ValidationSchema = (isCategoryEnabled) =>
  Yup.object().shape({
    name: Yup.string().required("Required"),
    locationId: Yup.string(),
    groupId: Yup.string(),
    priority: Yup.number()
      .nullable()
      .integer("Amount must be a valid whole number")
      .typeError("Amount must be a valid number"),
    type: Yup.string().required("Required"),
    price: decimal().nullable().typeError("Amount must be a valid number").required("Required"),
    ...(isCategoryEnabled ? { categoryId: Yup.string().required("Required") } : {}),
  });

 export type ProductInitialValues = {
   id?: string;
   name: string;
   locationId?: string;
   type: string;
   categoryId?: string;
   groupId?: string;
   priority: number | null;
   price: Decimal | string;
 };

type Props = {
  initialValues: ProductInitialValues;
  error: string;
  locationHelper: SearchableModelHelper;
  productGroupsHelper: SearchableModelHelper;
  productCategoriesHelper: SearchableModelHelper;
  isCategoryEnabled: boolean;
  isAgent: boolean;
  onSubmit: (data: FormikValues) => void;
  onClose: () => void;
};

const ProductForm: FC<Props> = ({
  initialValues,
  error,
  locationHelper,
  productGroupsHelper,
  productCategoriesHelper,
  isCategoryEnabled,
  isAgent,
  onSubmit,
  onClose,
}) => {

  return (
    <FormikForm
      initialValues={initialValues}
      onSubmit={(data) => onSubmit(ValidationSchema(!!productCategoriesHelper).cast(data))}
      validationSchema={ValidationSchema}
      enableReinitialize
    >
      {({ values: { type, groupId, priority }, setFieldValue }) => {
        useEffect(() => {
          if (priority === "") setFieldValue("priority", null);
        }, [priority]);

        return (
          <Form className="form__cont">
            <Field name="name" label="Name" type="text" component={Input} className="input input--bubble" />
            {isAgent && (
              <Field
                name="locationId"
                label="Location"
                component={ModelSelect}
                selectHelper={locationHelper}
                className="input input--bubble"
              />
            )}
            {isCategoryEnabled && (
              <Field
                name="categoryId"
                label="Product category"
                component={ModelSelect}
                selectHelper={productCategoriesHelper}
                className="input input--bubble"
              />
            )}
            <Field
              name="type"
              label="Product type"
              component={Select}
              options={ProductTypeOptions.selectOptions()}
              className="input input--bubble"
              cbChange={(_, type) => {
                type.value === ProductTypes.CUSTOM ? setFieldValue("price", 0) : setFieldValue("price", "");
                if (type.value !== ProductTypes.ITEM) {
                  setFieldValue("groupId", "");
                  setFieldValue("priority", null);
                }
              }}
            />
            {type === ProductTypes.ITEM && (
              <Field
                name="groupId"
                label="Product group"
                component={ModelSelect}
                selectHelper={productGroupsHelper}
                className="input input--bubble"
              />
            )}
            <Field
              name="priority"
              label="Priority"
              component={Input}
              className="input input--bubble"
              inputHint={"Priority is a number starting from 0, where 0 is the first item in the group."}
            />
            {type !== ProductTypes.CUSTOM && (
              <Field
                name="price"
                label="Price"
                component={Input}
                className="input input--bubble input--sign"
                inputSign="$"
              />
            )}
            {error && <div className="form__note form__note--err">{error}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "40px" }}>
              <Button title={"Cancel"} buttonStyle={buttonStyle.WARN} buttonSize={buttonSize.LG} onClick={onClose} />
              <button className="btn btn--prim btn--lg" type="submit">
                Submit
              </button>
            </div>
          </Form>
        );
      }}
    </FormikForm>
  );
};

export default ProductForm;
